import { AxiosProgressEvent } from 'axios';
import { api, ApiError, axiosInstance } from './api';
import { fileTypeFromBlob } from 'file-type';

export interface ResourceResponse {
  id: number;
  name: string;
  thumbnail: string;
  type: string;
  agency: {
    id: number;
    name: string;
    createdAt: string;
    updatedAt: string;
  };
  category: {
    id: number;
    name: string;
    createdAt: string;
    updatedAt: string;
  };
  createdAt: string;
  updatedAt: string;
  sourcePath?: string;
}

export interface ResourcesResponse {
  resources: ResourceResponse[];
  pageNumber: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
}

export interface GetResourcesParams {
  limit?: number;
  offset?: number;
  agencyId?: number;
  categoryId?: number;
  type?: string;
}

export const resources = {
  getList: async (params: GetResourcesParams): Promise<ResourcesResponse> => {
    try {
      const response = await api.get<ResourcesResponse>('/resources', { params });
      return response;
    } catch (error) {
      const apiError = error as ApiError;
      throw apiError;
    }
  },
  create: async (data: FormData): Promise<ResourceResponse> => {
    try {
      const response = await api.post('/resources', data, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response;
    } catch (error) {
      const apiError = error as ApiError;
      throw apiError;
    }
  },
  delete: async (id: number): Promise<void> => {
    try {
      await api.delete(`/resources/${id}`);
    } catch (error) {
      const apiError = error as ApiError;
      throw apiError;
    }
  },
  deleteMultiple: async (ids: number[]): Promise<void> => {
    try {
      await api.delete('/resources', {
        resourceIds: ids,
      });
    } catch (error) {
      const apiError = error as ApiError;
      throw apiError;
    }
  },
  downloadMultiple: async (resourceIds: number[]): Promise<Blob> => {
    try {
      const response = await api.get<Blob>('/resources/download', {
        params: { resourceIds },
        paramsSerializer: {
          indexes: null,
          serialize: (params: any) => {
            return `resourceIds=${params.resourceIds.join(',')}`;
          },
        },
        responseType: 'blob',
      });
      return response;
    } catch (error) {
      const apiError = error as ApiError;
      throw apiError;
    }
  },
  download: async (
    resourceId: number,
    onProgress?: (value: number) => void
  ): Promise<{ blob: Blob; filename: string }> => {
    try {
      if (onProgress) {
        onProgress(0);
      }
      const response = await axiosInstance.get<Blob>(`/resources/download/${resourceId}`, {
        responseType: 'blob',
        headers: {
          Accept: '*/*',
        },
        onDownloadProgress: (event: AxiosProgressEvent) => {
          if (!onProgress) return;
          const directProgress = typeof event.progress === 'number' ? event.progress : null;
          if (directProgress && directProgress > 0) {
            onProgress(Math.min(100, Math.max(0, Math.round(directProgress * 100))));
            return;
          }

          let total = typeof event.total === 'number' && event.total > 0 ? event.total : undefined;

          if (!total) {
            const progressEvent = event.event as ProgressEvent<EventTarget> | undefined;
            if (progressEvent && progressEvent.total > 0) {
              total = progressEvent.total;
            }

            if (!total) {
              const request = progressEvent?.target as XMLHttpRequest | undefined;
              const header = request?.getResponseHeader?.('content-length');
              if (header) {
                const parsed = parseInt(header, 10);
                if (!Number.isNaN(parsed) && parsed > 0) {
                  total = parsed;
                }
              }
            }
          }

          if (!total || total <= 0) return;

          const value = Math.round((event.loaded / total) * 100);
          onProgress(Math.min(100, Math.max(0, value)));
        },
      });

      const contentDisposition = response.headers['content-disposition'];
      if (!contentDisposition) {
        throw new Error('Content-Disposition header is missing');
      }

      let filename = contentDisposition.split('filename=')[1]?.replace(/["']/g, '');
      if (!filename || !filename.includes('.')) {
        const fileTypeResult = await fileTypeFromBlob(response.data);
        if (!filename) {
          filename = `resource-${resourceId}`;
        }
        if (fileTypeResult) {
          filename = `${filename}.${fileTypeResult.ext}`;
        }
      }

      return { blob: response.data, filename };
    } catch (error) {
      if (error instanceof Error && error.message === 'Content-Disposition header is missing') {
        throw new Error('Server did not provide proper file information');
      }
      const apiError = error as ApiError;
      throw apiError;
    }
  },
};
